import type { UseQueryResult } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import type {
  FilterParamsHandle,
  ParamsConstraint,
} from "@/hooks/use-filter-params";
import type { Nullable } from "@/types/utils";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";

import {
  isRecoverableStatusCode,
  type RecoveryState,
  type StaleFilterCleaner,
} from "./types";

interface TableResponseWithAvailableFilters<TAvailableFilters> {
  availableFilters: TAvailableFilters;
}

export interface UseTableQueryWithRecoveryOptions<
  TData extends TableResponseWithAvailableFilters<TAvailableFilters>,
  TParams extends ParamsConstraint,
  TAvailableFilters,
> {
  /** The underlying query result from useAuthedQuery */
  query: UseQueryResult<TData>;
  /** The filter params handle (mutable) for updating state */
  filterParams: FilterParamsHandle<TParams>;
  /** Function to execute a param-less fallback request for error recovery */
  fetchWithoutParams: () => Promise<TData>;
  /** Function to compare and clean stale filters */
  cleanStaleFilters: StaleFilterCleaner<TParams, TAvailableFilters>;
  /** Whether to enable recovery and cleanup behavior (default: true) */
  enabled?: boolean;
}

export type UseTableQueryWithRecoveryResult<TData> = UseQueryResult<TData> & {
  /** Current state of error recovery attempts */
  recoveryState: RecoveryState;
};

/**
 * Wrapper hook that adds resilience to BE-paginated table queries.
 *
 * Features:
 * 1. Error recovery: On non-auth errors, retries without params. If successful,
 *    clears params to defaults (assumes original params were corrupted).
 * 2. Stale filter cleanup: Compares availableFilters from response with current
 *    params and surgically removes any stale values.
 */
export const useTableQueryWithRecovery = <
  TData extends TableResponseWithAvailableFilters<TAvailableFilters>,
  TParams extends ParamsConstraint,
  TAvailableFilters,
>({
  query,
  filterParams,
  fetchWithoutParams,
  cleanStaleFilters,
  enabled = true,
}: UseTableQueryWithRecoveryOptions<
  TData,
  TParams,
  TAvailableFilters
>): UseTableQueryWithRecoveryResult<TData> => {
  const [recoveryState, setRecoveryState] = useState<RecoveryState>({
    isRecovering: false,
    recoveryAttempted: false,
    lastError: null,
  });

  // Track whether we've already processed the current successful response
  const lastProcessedDataRef = useRef<Nullable<TData>>(null);
  // Prevent multiple recovery attempts for the same error
  const recoveryAttemptedRef = useRef(false);

  const { data, error, isError, isSuccess } = query;

  // Memoize the recovery function to keep effect deps stable
  const attemptRecovery = useCallback(async () => {
    setRecoveryState((prev) => ({
      ...prev,
      isRecovering: true,
      lastError: isAxiosError(error) ? error : null,
    }));

    try {
      await fetchWithoutParams();

      // Param-less fetch succeeded: original params were likely corrupted
      filterParams.clear();
      setRecoveryState({
        isRecovering: false,
        recoveryAttempted: true,
        lastError: null,
      });
    } catch {
      // Param-less fetch also failed: server issue, not params
      setRecoveryState((prev) => ({
        ...prev,
        isRecovering: false,
        recoveryAttempted: true,
      }));

      showUpwageNotification(
        "There was a problem communicating with the server.",
        NotificationTypes.Error,
      );
    }
  }, [error, fetchWithoutParams, filterParams]);

  // Error Recovery Effect
  useEffect(() => {
    if (!enabled || !isError || !error || recoveryAttemptedRef.current) {
      return;
    }

    // Check if error is recoverable (not 401/403/404)
    const status = isAxiosError(error)
      ? (error.response?.status ?? error.status)
      : undefined;

    if (!isRecoverableStatusCode(status)) {
      return;
    }

    recoveryAttemptedRef.current = true;
    queueMicrotask(() => {
      void attemptRecovery();
    });
  }, [enabled, error, isError, attemptRecovery]);

  // Reset recovery tracking when query succeeds
  useEffect(() => {
    if (isSuccess) {
      recoveryAttemptedRef.current = false;
      if (recoveryState.recoveryAttempted) {
        queueMicrotask(() => {
          setRecoveryState({
            isRecovering: false,
            recoveryAttempted: false,
            lastError: null,
          });
        });
      }
    }
  }, [isSuccess, recoveryState.recoveryAttempted]);

  // Stale Filter Cleanup Effect
  useEffect(() => {
    if (!enabled || !isSuccess || !data) {
      return;
    }

    // Avoid reprocessing the same data
    if (lastProcessedDataRef.current === data) {
      return;
    }

    const currentParams = filterParams.toObject();
    const { cleaned, hadStaleValues } = cleanStaleFilters(
      currentParams,
      data.availableFilters,
    );

    if (hadStaleValues) {
      // Update filter params with cleaned values (triggers re-fetch)
      filterParams.replaceAll(cleaned as TParams);
    }

    lastProcessedDataRef.current = data;
  }, [enabled, data, isSuccess, cleanStaleFilters, filterParams]);

  // Reset processed data ref when filter params change externally
  const paramsString = filterParams.toString();

  useEffect(() => {
    lastProcessedDataRef.current = null;
  }, [paramsString]);

  return {
    ...query,
    recoveryState,
  };
};
